import { menuIconElement, type MenuIconId } from '../content/menu-icons.js';
import { audio } from '../audio/audio.js';

/**
 * Tiny DOM helpers. Deliberately not a framework: this game has about a dozen screens, most of
 * which are a few enormous buttons, and the drive screen is a Pixi canvas rather than DOM at all.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  options: {
    readonly className?: string;
    readonly text?: string;
    readonly children?: readonly (HTMLElement | null)[];
    readonly attrs?: Readonly<Record<string, string>>;
  } = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (options.className !== undefined) node.className = options.className;
  if (options.text !== undefined) node.textContent = options.text;
  for (const [key, value] of Object.entries(options.attrs ?? {})) node.setAttribute(key, value);
  for (const child of options.children ?? []) if (child !== null) node.append(child);
  return node;
}

/**
 * What a press *is*, in one place: no default, a sound, then the action.
 *
 * **`pointerdown` rather than `click`**: a click fires only on release and, on iOS Safari, can lag a touch
 * by 100-300ms. The design budget for visible feedback is 100ms, and a three-year-old who presses and sees
 * nothing presses harder somewhere else.
 *
 * **The sound is here rather than at the call sites** because that is the only way it can be true of every
 * button. It was written screen by screen before this and the Garage never got one, so the shelf of her own
 * cars was the one place in the game where pressing something made no noise at all - which reads as a
 * broken button, not as a quiet one. There is nothing to remember now: a menu button made any other way is
 * the bug, and `tests/press-sound.test.ts` presses every button on every menu to say so.
 *
 * It plays *before* `onPress`, deliberately. The action usually swaps the screen, and the sound belongs to
 * the press rather than to what the press led to.
 */
function onPointerDown(button: HTMLButtonElement, onPress: () => void): void {
  button.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    audio.press();
    onPress();
  });
}

/**
 * A big button.
 *
 * Presses on `pointerdown`, with the press sound - see `onPointerDown`.
 */
export function bigButton(
  label: string,
  onPress: () => void,
  options: {
    readonly variant?: 'go' | 'quiet';
    readonly className?: string;
    /** The drawn icon that should replace `label`; the glyph is only ever the fallback. */
    readonly icon?: MenuIconId;
  } = {},
): HTMLButtonElement {
  const classes = ['big-button'];
  if (options.variant === 'go') classes.push('big-button--go');
  if (options.variant === 'quiet') classes.push('big-button--quiet');
  if (options.className !== undefined) classes.push(options.className);

  const button = el('button', { className: classes.join(' '), text: label });
  button.type = 'button';
  onPointerDown(button, onPress);
  if (options.icon !== undefined) applyMenuIcon(button, options.icon);
  return button;
}

/**
 * Swap a glyph for its drawn icon when the icon lands.
 *
 * The glyph goes in first and stays until - or unless - the picture arrives, so the button is complete
 * and pressable from the first frame and a missing file costs the picture rather than the control. Same
 * pattern as the Lookout's photo of her own car.
 */
export function applyMenuIcon(host: HTMLElement, id: MenuIconId): void {
  void menuIconElement(id).then((svg) => {
    /*
     * It replaces the *glyph*, never real content. The drive slot swaps in a photograph of her own car,
     * and whichever of the two lands first, the photograph has to win - a generic car icon arriving
     * afterwards and covering her drawing would be the worst outcome on that screen.
     */
    if (svg === null || host.querySelector('svg, img') !== null) return;
    host.replaceChildren(svg);
  });
}

/**
 * A picture button: no label, no text, just a thing to recognise and press.
 *
 * The menus had four hand-rolled copies of this - hub buttons, chooser cards, the collection's hit
 * target - each remembering for itself that presses fire on `pointerdown` and that the default has to
 * be prevented. One of them forgetting is not a bug anybody would see in review; it is a button that
 * lags a touch by 300ms on iOS Safari, or a long-press that pops a selection menu over the game. It also
 * carries the press sound, which is the half of it nobody remembered - see `onPointerDown`.
 *
 * `bigButton` stays for the glyph buttons in the loop screens - a tick, a paw - which are text.
 */
export function pictureButton(
  content: readonly (HTMLElement | SVGElement)[],
  onPress: () => void,
  options: {
    readonly className?: string;
    readonly title?: string;
    readonly icon?: MenuIconId;
  } = {},
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  if (options.className !== undefined) button.className = options.className;
  // Not a label she can read: `title` is where the detail goes for whoever is looking after her.
  if (options.title !== undefined) button.title = options.title;
  for (const child of content) button.append(child);
  onPointerDown(button, onPress);
  if (options.icon !== undefined) applyMenuIcon(button, options.icon);
  return button;
}

/**
 * Glyph vocabulary. These must never collide: the back paw and the confirm button were the same
 * glyph in the first draft, which to a pre-reader is two identical buttons doing opposite things.
 * Distinct glyph *and* distinct colour, so it reads correctly before she learns either.
 *
 * These are now the *fallback* rather than what she sees: every one of them has a drawn icon in the
 * content pack (`GLYPH_ICON` below, `src/content/menu-icons.ts` for why). They stay because a glyph
 * needs no network, so a button is complete on its first frame and survives a missing file.
 */
export const GLYPH = {
  /** Green: yes, go, continue. */
  confirm: '✔',
  /**
   * Green: start, go, carry on.
   *
   * Three consumers, and they are deliberately the same shape: the GO pedal, reverse (the same triangle
   * mirrored in CSS), and the resume disc on the pause veil. "Press this and the car moves again" is one
   * idea, and drawing it twice would be how the vocabulary grows two words for it.
   */
  start: '▶',
  /** Quiet grey, top-left corner, every screen: back. Never used for anything else. */
  back: '\u{1F43E}',
  /** Colour / re-colour a car. */
  crayon: '\u{1F58D}',
  /** Drive a car. */
  car: '\u{1F697}',
  garage: '\u{1F3E1}',
  /** Throw away. Only ever *asks*; the second tap is a separate, deliberate press. */
  bin: '\u{1F5D1}',
  outdoors: '\u{1F3DE}',
  treat: '\u{1F36A}',
} as const;

/**
 * The drawn icon behind each glyph.
 *
 * Two of these are deliberately *not* the emoji's own meaning, because the emoji were wrong: `garage`
 * was a house, which is what the Lookout is and what "home" means on the arrival beat, and `treat` was
 * a biscuit, which is a thing she can be sent to fetch rather than a shelf of prizes. The drawn set
 * says a garage and a treat jar. See `src/content/menu-icons.ts`.
 */
export const GLYPH_ICON: Readonly<Record<keyof typeof GLYPH, MenuIconId>> = {
  confirm: 'confirm',
  start: 'start',
  back: 'back',
  crayon: 'crayon',
  car: 'car',
  garage: 'garage',
  bin: 'bin',
  outdoors: 'freeplay',
  treat: 'prizes',
};

/**
 * What a screen shows when its content will not load.
 *
 * A sad pup and nothing else. The two failure paths here used to print a sentence aimed at whoever was
 * debugging - "This car is missing its drawing. Check public/content/packs/..." - on screens the player
 * is looking at. She cannot read it, so to her it was the same as a blank screen with extra noise in it.
 *
 * The detail for the parent goes to the console and to `title`, neither of which is on screen. Every
 * screen that can show this also has a back paw, so a failure is always something she can leave.
 */
export function loadFailed(detailForParent: string): HTMLElement {
  const host = el('div', {
    className: 'load-failed',
    children: [el('div', { className: 'load-failed-pup', text: '\u{1F436}' })],
  });
  host.title = detailForParent;
  return host;
}

/** The back affordance: same paw, same corner, every screen. */
export function backPaw(onPress: () => void): HTMLButtonElement {
  return bigButton(GLYPH.back, onPress, {
    variant: 'quiet',
    className: 'back-paw',
    icon: GLYPH_ICON.back,
  });
}

/**
 * Whether the device has asked for less motion.
 *
 * The stylesheet already honours this for the hub's drifting clouds and breathing title, but the drive
 * scene is a canvas and CSS cannot reach inside it - so the one thing in there that needs to ask, the
 * emergency lights, asks here rather than growing its own copy of the query.
 *
 * Guarded because a browser without `matchMedia` has to give a playing game rather than a broken one,
 * which is the same bargain every entry point in the audio director takes.
 */
export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  } catch {
    return false;
  }
}

/**
 * A timer that survives unmount. Screens must return their disposers, because a stray timer firing
 * after a screen is gone dispatches an event from a phase the game has already left.
 */
export function timeout(ms: number, fn: () => void): () => void {
  const handle = window.setTimeout(fn, ms);
  return () => window.clearTimeout(handle);
}
